import PageHero from "../components/sections/PageHero";
import CTABlock from "../components/sections/CTABlock";

const approach = [
  {
    title: "Research-Led Preparation",
    desc: "Every matter begins with understanding the facts, documents, legal position and risks.",
  },
  {
    title: "Strategy-Focused Advice",
    desc: "The firm focuses on legal solutions that are legally sound and commercially practical.",
  },
  {
    title: "Outcome-Oriented Representation",
    desc: "Lex Polaris supports clients with clear direction, strong documentation and focused representation before relevant forums.",
  },
];

const definers = [
  "Sharp legal analysis",
  "Strategic dispute handling",
  "Strong documentation",
  "Commercially practical advice",
  "Ethical client representation",
  "Clear communication",
];

export default function AboutPage() {
  return (
    <>
      <PageHero
        eyebrow="About the Firm"
        title="About Lex Polaris"
        subtitle="A research-driven and strategy-focused law practice providing litigation, dispute resolution and corporate advisory services in Delhi NCR."
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "About" }]}
      />

      {/* INTRO */}
      <section className="bg-ivory section-y" data-testid="about-intro">
        <div className="container-px max-w-[1400px] mx-auto grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="overline mb-6">Firm Introduction</div>
            <h2 className="h-display text-4xl md:text-5xl text-navy">
              A practice built on clarity, preparation and focused representation.
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <div className="editorial-rule mb-8" />
            <p className="body-lg text-charcoal/80 mb-6">
              Lex Polaris, Advocates &amp; Solicitors is a research-driven and strategy-focused law practice
              based in Delhi NCR. The firm provides litigation, dispute resolution and corporate advisory
              services to individuals, businesses, start-ups, real estate stakeholders and corporate clients.
            </p>
            <p className="body-lg text-charcoal/75">
              The firm’s approach is built around legal clarity, detailed preparation, strategic thinking and
              practical outcomes.
            </p>
          </div>
        </div>
      </section>

      {/* APPROACH */}
      <section className="bg-white border-y border-soft-grey section-y" data-testid="about-approach">
        <div className="container-px max-w-[1400px] mx-auto">
          <div className="max-w-3xl mb-14 md:mb-20">
            <div className="overline mb-6">Our Approach</div>
            <h2 className="h-display text-4xl md:text-5xl lg:text-6xl text-navy">
              Strategic counsel. Practical direction. Focused representation.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-px bg-soft-grey border border-soft-grey">
            {approach.map((a, idx) => (
              <div
                key={a.title}
                data-testid={`about-approach-${idx}`}
                className="bg-white p-9 md:p-11 hover:bg-ivory transition-colors"
              >
                <div className="font-serif text-gold text-base mb-6">{String(idx + 1).padStart(2, "0")}</div>
                <h3 className="font-serif text-2xl text-navy mb-4 leading-tight">{a.title}</h3>
                <p className="text-charcoal/70 leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO WE WORK WITH */}
      <section className="bg-navy text-ivory section-y" data-testid="about-clients">
        <div className="container-px max-w-[1400px] mx-auto grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="overline text-gold mb-6">Who We Work With</div>
            <h2 className="h-display text-4xl md:text-5xl text-ivory">
              Diverse clients. Focused counsel.
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <ul className="divide-y divide-ivory/10 border-t border-b border-ivory/10">
              {[
                "Individuals",
                "Businesses",
                "Start-ups",
                "Real Estate Stakeholders",
                "Corporate Clients",
              ].map((c, i) => (
                <li
                  key={c}
                  className="flex items-center justify-between py-5"
                  data-testid={`about-client-${i}`}
                >
                  <span className="font-serif text-2xl md:text-3xl text-ivory">{c}</span>
                  <span className="font-serif text-gold text-sm">{String(i + 1).padStart(2, "0")}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* WHAT DEFINES US */}
      <section className="bg-ivory section-y" data-testid="about-defines">
        <div className="container-px max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-12 gap-10 mb-12 md:mb-16">
            <div className="md:col-span-6">
              <div className="overline mb-6">What Defines Us</div>
              <h2 className="h-display text-4xl md:text-5xl text-navy">Six values that shape our work.</h2>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-soft-grey border border-soft-grey">
            {definers.map((d, i) => (
              <div
                key={d}
                data-testid={`about-define-${i}`}
                className="bg-white p-8 md:p-10 flex items-start gap-5"
              >
                <span className="font-serif text-gold text-sm pt-2">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-serif text-xl md:text-2xl text-navy leading-tight">{d}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABlock
        eyebrow="Consultation"
        heading="Looking for strategic legal guidance?"
        body="Connect with Lex Polaris for clear, research-driven advice tailored to your matter."
        primaryLabel="Contact Lex Polaris"
        primaryTo="/contact"
        secondaryLabel="View Expertise"
        secondaryHref="/expertise"
      />
    </>
  );
}
